import { useCallback, useEffect, useRef, useState } from 'react';
import type { Room } from 'livekit-client';
import { LiveKitService } from '../services/LiveKitService';
import type { LiveKitConnectionOptions, LiveKitEvents, LiveKitMode, LiveKitState } from '../types';

export interface LiveKitHookOptions extends LiveKitEvents {
  autoReconnect?: boolean;
  reconnectAttempts?: number;
  reconnectDelay?: number;
}

/**
 * Hook for managing LiveKit video connections with automatic reconnection
 */
export const useLiveKit = (options?: LiveKitHookOptions) => {
  const [state, setState] = useState<LiveKitState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<string[]>([]);
  const [room, setRoom] = useState<Room | null>(null);
  const [currentMode, setCurrentMode] = useState<LiveKitMode | null>(null);

  const serviceRef = useRef<LiveKitService | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const lastConnectionOptionsRef = useRef<LiveKitConnectionOptions | null>(null);
  const isReconnectingRef = useRef(false);
  const lastStateChangeRef = useRef<number>(0);

  // Extract options with defaults
  const {
    autoReconnect = true,
    reconnectAttempts = 3, // Reduce attempts to prevent endless loops
    reconnectDelay = 5000, // Increase delay to reduce frequency
    ...events
  } = options || {};

  // Store events in ref to avoid dependency issues
  const eventsRef = useRef(events);
  eventsRef.current = events;

  /**
   * Clear any pending reconnection attempts
   */
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    isReconnectingRef.current = false;
  }, []);

  /**
   * Internal function to perform reconnection
   */
  const performReconnection = useCallback(async () => {
    if (!autoReconnect || !lastConnectionOptionsRef.current || isReconnectingRef.current) {
      return;
    }

    const maxAttempts = reconnectAttempts;
    const currentAttempt = reconnectAttemptsRef.current;

    if (currentAttempt >= maxAttempts) {
      console.log(`❌ Max reconnection attempts (${maxAttempts}) reached. Giving up.`);
      setState('error');
      setError(`Failed to reconnect after ${maxAttempts} attempts`);
      isReconnectingRef.current = false;
      return;
    }

    isReconnectingRef.current = true;
    reconnectAttemptsRef.current += 1;
    const delay = reconnectDelay * Math.pow(1.5, currentAttempt); // Exponential backoff

    console.log(`🔄 Attempting reconnection ${currentAttempt + 1}/${maxAttempts} in ${delay}ms...`);

    reconnectTimeoutRef.current = setTimeout(async () => {
      try {
        const options = lastConnectionOptionsRef.current;
        if (options && isReconnectingRef.current) {
          console.log(`🔄 Reconnection attempt ${currentAttempt + 1}/${maxAttempts}`);

          // Save options before disconnect (disconnect clears lastConnectionOptionsRef)
          const savedOptions = { ...options };

          if (serviceRef.current) {
            await serviceRef.current.disconnect();
          }

          // Restore options after disconnect
          lastConnectionOptionsRef.current = savedOptions;

          const service = new LiveKitService({
            onStateChange: (newState) => {
              setState(newState);
              eventsRef.current?.onStateChange?.(newState);

              // Handle automatic reconnection on disconnect - but only if not already reconnecting
              // Add debounce to prevent rapid state changes from triggering multiple reconnections
              const now = Date.now();
              if (
                newState === 'disconnected' &&
                autoReconnect &&
                !isReconnectingRef.current &&
                now - lastStateChangeRef.current > 1000
              ) {
                lastStateChangeRef.current = now;
                console.log('🔄 Connection lost, attempting to reconnect...');
                setTimeout(() => performReconnection(), 1000);
              } else if (newState === 'connected') {
                // Reset reconnection attempts on successful connection
                reconnectAttemptsRef.current = 0;
                clearReconnectTimeout();
                lastStateChangeRef.current = now;
                console.log('✅ Successfully reconnected');
              }
            },
            onError: (livekitError) => {
              setError(livekitError.message);
              eventsRef.current?.onError?.(livekitError);

              // Attempt reconnection on error if auto-reconnect is enabled and not already reconnecting
              if (autoReconnect && !isReconnectingRef.current) {
                console.log('❌ Connection error, attempting to reconnect...');
                setTimeout(() => performReconnection(), 100);
              }
            },
            onParticipantJoined: (participantId) => {
              setParticipants((prev) => [...prev, participantId]);
              eventsRef.current?.onParticipantJoined?.(participantId);
            },
            onParticipantLeft: (participantId) => {
              setParticipants((prev) => prev.filter((id) => id !== participantId));
              eventsRef.current?.onParticipantLeft?.(participantId);
            },
          });

          const result = await service.connect(savedOptions);

          if (result.success) {
            serviceRef.current = service;
            setRoom(result.room || null);
            setError(null);
            // Reset reconnection attempts on successful connection
            reconnectAttemptsRef.current = 0;
            isReconnectingRef.current = false;
          } else {
            setError(result.error || 'Failed to connect');
            isReconnectingRef.current = false;
            if (autoReconnect) {
              setTimeout(() => performReconnection(), 100);
            }
          }
        }
      } catch (error) {
        console.error(`❌ Reconnection attempt ${currentAttempt + 1} failed:`, error);
        isReconnectingRef.current = false;
        // Try again only if we haven't exceeded max attempts
        if (reconnectAttemptsRef.current < reconnectAttempts) {
          performReconnection();
        }
      }
    }, delay);
  }, [autoReconnect, reconnectAttempts, reconnectDelay, clearReconnectTimeout]);

  /**
   * Connect to a LiveKit room
   */
  const connect = useCallback(
    async (roomName: string, participantName: string, mode: LiveKitMode) => {
      try {
        // Clear any pending reconnection attempts
        clearReconnectTimeout();

        if (serviceRef.current) {
          await serviceRef.current.disconnect();
        }

        const service = new LiveKitService({
          onStateChange: (newState) => {
            setState(newState);
            eventsRef.current?.onStateChange?.(newState);

            // Handle automatic reconnection on disconnect - but only if not already reconnecting
            // Add debounce to prevent rapid state changes from triggering multiple reconnections
            const now = Date.now();
            if (
              newState === 'disconnected' &&
              autoReconnect &&
              !isReconnectingRef.current &&
              now - lastStateChangeRef.current > 1000
            ) {
              lastStateChangeRef.current = now;
              console.log('🔄 Connection lost, attempting to reconnect...');
              setTimeout(() => performReconnection(), 1000);
            } else if (newState === 'connected') {
              // Reset reconnection attempts on successful connection
              reconnectAttemptsRef.current = 0;
              clearReconnectTimeout();
              lastStateChangeRef.current = now;
              console.log('✅ Successfully connected');
            }
          },
          onError: (livekitError) => {
            setError(livekitError.message);
            eventsRef.current?.onError?.(livekitError);

            // Attempt reconnection on error if auto-reconnect is enabled and not already reconnecting
            if (autoReconnect && !isReconnectingRef.current) {
              console.log('❌ Connection error, attempting to reconnect...');
              setTimeout(() => performReconnection(), 100);
            }
          },
          onParticipantJoined: (participantId) => {
            setParticipants((prev) => [...prev, participantId]);
            eventsRef.current?.onParticipantJoined?.(participantId);
          },
          onParticipantLeft: (participantId) => {
            setParticipants((prev) => prev.filter((id) => id !== participantId));
            eventsRef.current?.onParticipantLeft?.(participantId);
          },
        });

        const connectionOptions: LiveKitConnectionOptions = {
          roomName,
          participantName,
          mode,
        };

        // Store connection options for reconnection
        lastConnectionOptionsRef.current = connectionOptions;

        const result = await service.connect(connectionOptions);

        if (result.success) {
          serviceRef.current = service;
          setRoom(result.room || null);
          setError(null);
          // Reset reconnection attempts on successful connection
          reconnectAttemptsRef.current = 0;
        } else {
          setError(result.error || 'Failed to connect');
          if (autoReconnect && !isReconnectingRef.current) {
            setTimeout(() => performReconnection(), 100);
          }
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to connect to LiveKit';
        setError(errorMessage);
        if (autoReconnect && !isReconnectingRef.current) {
          setTimeout(() => performReconnection(), 100);
        }
        throw err;
      }
    },
    [autoReconnect, clearReconnectTimeout, performReconnection]
  );

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(async () => {
    if (!lastConnectionOptionsRef.current) {
      throw new Error('No previous connection to reconnect to');
    }

    const options = lastConnectionOptionsRef.current;
    reconnectAttemptsRef.current = 0; // Reset attempts for manual reconnect
    await connect(options.roomName, options.participantName, options.mode);
  }, [connect]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Disconnect from the room
   */
  const disconnect = useCallback(async () => {
    // Clear any pending reconnection attempts
    clearReconnectTimeout();
    lastConnectionOptionsRef.current = null; // Clear stored options to prevent auto-reconnect
    reconnectAttemptsRef.current = 0; // Reset attempts

    if (serviceRef.current) {
      await serviceRef.current.disconnect();
      serviceRef.current = null;
      setRoom(null);
      setParticipants([]);
      setError(null);
      setCurrentMode(null);
    }
  }, [clearReconnectTimeout]);

  /**
   * Start streaming (connect as streamer)
   */
  const startStreaming = useCallback(
    async (roomName: string, participantName: string) => {
      setCurrentMode('stream');
      return connect(roomName, participantName, 'stream');
    },
    [connect]
  );

  /**
   * Join as viewer
   */
  const joinAsViewer = useCallback(
    async (roomName: string, participantName: string) => {
      setCurrentMode('join');
      return connect(roomName, participantName, 'join');
    },
    [connect]
  );

  /**
   * Get the current room
   */
  const getRoom = useCallback(() => {
    return serviceRef.current?.getRoom() || null;
  }, []);

  /**
   * Check if connected
   */
  const isConnected = state === 'connected';
  const isConnecting = state === 'connecting';
  const hasError = state === 'error';

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearReconnectTimeout();
      if (serviceRef.current) {
        serviceRef.current.dispose();
        serviceRef.current = null;
      }
    };
  }, [clearReconnectTimeout]);

  return {
    // Actions
    connect,
    disconnect,
    reconnect,
    clearError,
    startStreaming,
    joinAsViewer,
    getRoom,

    // State
    state,
    error,
    participants,
    room,
    currentMode,
    isConnected,
    isConnecting,
    hasError,

    // Reconnection info
    reconnectAttempts: reconnectAttemptsRef.current,
    maxReconnectAttempts: reconnectAttempts,
  };
};
